import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, Transition, Popover } from '@headlessui/react';
import { toast } from 'react-toastify';
import { HeartIcon, ShoppingBagIcon, UserIcon, ArrowLeftOnRectangleIcon, Cog6ToothIcon, MagnifyingGlassIcon, UserCircleIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { getMenuData } from '../utils/navigationData';
import MobileMenu from './MobileMenu';

function PopoverMenuItem({ item }) {
    return (
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button className="group inline-flex items-center rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:text-brand-green focus:outline-none">
              <span>{item.title}</span>
            </Popover.Button>
            <Transition as={Fragment} enter="transition ease-out duration-200" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-150" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute left-1/2 z-10 mt-3 w-screen max-w-sm -translate-x-1/2 transform px-4 sm:px-0 lg:max-w-max">
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative grid gap-8 bg-white p-7 lg:grid-cols-2">
                    {item.subCategories.map((sub) => (
                      <div key={sub.title} className="-m-3 flex items-start rounded-lg p-3 transition duration-150 ease-in-out hover:bg-gray-50">
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-900">{sub.title}</p>
                          <div className="mt-1 flex flex-col space-y-1">
                            {sub.links.map((link) => (
                               <Link key={link.name} to={link.path} onClick={close} className="text-sm text-gray-500 hover:text-brand-green"> {link.name} </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    )
}

function Header() {
  const { session, user, logout, isAdmin } = useAuth();
  const { cartItemCount } = useCart();
  const { favoritesCount } = useFavorites();
  const navigate = useNavigate();
  
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuData, setMenuData] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const inputRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Başarıyla çıkış yaptınız.');
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
      setIsSearchOpen(false);
      setSearchTerm('');
    }
  };

  // Menü verilerini yükle
  useEffect(() => {
    const loadMenuData = async () => {
      try {
        const data = await getMenuData();
        setMenuData(data);
      } catch (error) {
        console.error('Menü yüklenirken hata:', error);
      } finally {
        setMenuLoading(false);
      }
    };

    loadMenuData();
  }, []);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
        inputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <header className="bg-brand-header sticky top-0 z-40 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <MobileMenu />
            <Link to="/" className="flex items-center ml-2 lg:ml-0">
              <img src={process.env.PUBLIC_URL + '/minela_kids_logo.jpg'} alt="Minela Kids Logo" className="h-10 md:h-12 w-auto rounded-md" />
            </Link>
          </div>

          <nav className="hidden lg:flex items-center space-x-6">
            {menuLoading ? (
              // Skeleton loading
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                </div>
              ))
            ) : (
              menuData.map((item) => (
                <PopoverMenuItem key={item.title} item={item} />
              ))
            )}
          </nav>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="flex items-center justify-end">
                <form onSubmit={handleSearchSubmit} className={`flex items-center transition-all duration-300 ease-in-out ${isSearchOpen ? 'w-40 sm:w-56' : 'w-0'}`}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onBlur={() => { if (!searchTerm) setIsSearchOpen(false); }}
                        placeholder="Ara..."
                        // DÜZELTME 1: Kapalıyken klavye ile odağı engelle
                        tabIndex={isSearchOpen ? 0 : -1}
                        // DÜZELTME 2: Kapalıyken fare olaylarını engelle ve sınıfı ayarla.
                        className={`w-full pl-2 pr-1 py-1 border-b-2 bg-transparent focus:outline-none text-sm transition-opacity duration-300 ${isSearchOpen ? 'opacity-100 border-brand-green' : 'opacity-0 border-transparent pointer-events-none'}`}
                    />
                </form>
                <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="p-1 text-gray-600 hover:text-brand-green">
                    <MagnifyingGlassIcon className="h-6 w-6" />
                </button>
            </div>

            <Link to="/favorites" className="relative text-gray-600 hover:text-brand-green">
              <HeartIcon className="h-6 w-6" />
              {favoritesCount > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{favoritesCount}</span>}
            </Link>
            <Link to="/cart" className="relative text-gray-600 hover:text-brand-green">
              <ShoppingBagIcon className="h-6 w-6" />
              {cartItemCount > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{cartItemCount}</span>}
            </Link>
            {session ? (
                 <Menu as="div" className="relative">
                   <Menu.Button className="flex items-center text-gray-600 hover:text-brand-green">
                     <UserCircleIcon className="h-7 w-7" />
                   </Menu.Button>
                   <Transition as={Fragment} enter="transition ease-out duration-100" enterFrom="transform opacity-0 scale-95" enterTo="transform opacity-100 scale-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100 scale-100" leaveTo="transform opacity-0 scale-95">
                     <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                       <div className="px-1 py-1">
                         <div className="px-3 py-2"><p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p></div>
                         <Menu.Item>{({ active }) => ( <NavLink to="/my-account" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <UserIcon className="mr-2 h-5 w-5" /> Hesabım </NavLink> )}</Menu.Item>
                         <Menu.Item>{({ active }) => ( <NavLink to="/my-account?tab=orders" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M8 11v6h8v-6M8 11H6a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2h-2" /></svg> Siparişlerim </NavLink> )}</Menu.Item>
                         <Menu.Item>{({ active }) => ( <NavLink to="/my-account?tab=returns" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <ArrowUturnLeftIcon className="mr-2 h-5 w-5" /> İadelerim </NavLink> )}</Menu.Item>
                         {/* YENİ: Kampanya geçmişi linki */}
                         <Menu.Item>{({ active }) => ( <NavLink to="/campaign-history" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> 💰 Kampanya Geçmişim </NavLink> )}</Menu.Item>
                         {/* KAMPANYA TAKİP: Kampanya takip linki */}
                         <Menu.Item>{({ active }) => ( <NavLink to="/campaign-tracking" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg> Kampanya Takibi </NavLink> )}</Menu.Item>
                         {isAdmin && (<Menu.Item>{({ active }) => ( <NavLink to="/admin" className={`${active ? 'bg-brand-green-light text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <Cog6ToothIcon className="mr-2 h-5 w-5" /> Yönetici Paneli </NavLink> )}</Menu.Item>)}
                         <Menu.Item>{({ active }) => ( <button onClick={handleLogout} className={`${active ? 'bg-red-500 text-white' : 'text-gray-900'} group flex rounded-md items-center w-full px-2 py-2 text-sm`}> <ArrowLeftOnRectangleIcon className="mr-2 h-5 w-5" /> Çıkış Yap </button> )}</Menu.Item>
                       </div>
                     </Menu.Items>
                   </Transition>
                 </Menu>
            ) : (
              <Link to="/auth" className="text-gray-600 hover:text-brand-green"><UserIcon className="h-6 w-6" /></Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;